/**
 * Donut — shared aspect. Circular ring progress indicator with a
 * center percentage label. Past use: WorkspaceHero's case-progress
 * indicator. Reusable wherever progress-as-circle makes sense.
 *
 * Renders an empty track + a stroke-dashed colored arc on top.
 * Center label defaults to the computed percentage; consumer can
 * override (e.g. "0/3" instead of "0%").
 */
import { Text, View } from "react-native";
import Svg, { Circle, G } from "react-native-svg";

import type { PaneTheme } from "../brand/paneThemes";

export type DonutProps = {
  theme: PaneTheme;
  /** 0..1 — drives the arc fill. Clamped to [0,1]. */
  pct: number;
  /** Overall diameter in px. Default 92. */
  size?: number;
  /** Stroke width in px. Default 7. */
  stroke?: number;
  /** Center label color (caller-specified so the donut works on
   *  light or dark surfaces). */
  labelColor: string;
  /** Override the center label. Defaults to "{pct}%". */
  label?: string;
};

export function Donut({
  theme,
  pct,
  size = 92,
  stroke = 7,
  labelColor,
  label,
}: DonutProps) {
  const inset = stroke / 2 + 2;
  const radius = (size - inset * 2) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(1, pct));
  const dashOffset = circumference * (1 - clamped);
  const computed = label ?? `${Math.round(clamped * 100)}%`;
  return (
    <View style={{ width: size, height: size }}>
      <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={theme.surfaceDividerFaint}
          strokeWidth={stroke}
        />
        <G rotation={-90} origin={`${size / 2}, ${size / 2}`}>
          <Circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={clamped === 0 ? theme.surfaceDividerStrong : theme.accent}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
          />
        </G>
      </Svg>
      <View
        pointerEvents="none"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: size,
          height: size,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text
          style={{
            fontFamily: theme.displayMediumFont,
            fontSize: Math.round(size * 0.26),
            color: labelColor,
          }}
        >
          {computed}
        </Text>
      </View>
    </View>
  );
}
